import {
  keepPreviousData as keepPreviousQueryData,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useCallback } from "react";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { recursiveCamelCaseCipher } from "../../postgrestApi";
import FetchClients, { ClientPaths } from "../client";
import { throwBackpackResponseError } from "../errors";
import { formatEquipmentExportCsv } from "./formatEquipmentExportCsv";
import {
  Equipment,
  SiteEquipmentPageParams,
  SiteEquipmentPageResponse,
} from "./types";
import {
  SITE_EQUIPMENT_PAGE_QUERY_KEY,
  SITE_EQUIPMENT_QUERY_KEY,
} from "./queryKeys";
import { siteEquipmentPageParamsToQuery } from "./siteEquipmentPageParams";
import {
  EQUIPMENT_SPECS_READS_HEADER_NAME,
  EQUIPMENT_SPECS_WRITES_HEADER_NAME,
  EquipmentRequestHeaders,
  useEquipmentSpecsReadHeaders,
  useEquipmentSpecsRolloutEnabled,
} from "./equipmentSpecsHeaders";

const site = {
  useQuery,
  usePageQuery,
  fetchAllPages,
  useSyncEquipment,
  useQuerySyncStatus,
  useStartSyncPreview,
  useQuerySyncPreview,
  useApplySyncPreview,
  useDownloadCSVExport,
};

export default site;

const getSiteEquipment = async (
  siteId: number,
  include_preview: boolean,
  headers: EquipmentRequestHeaders,
  building?: string | null
): Promise<Equipment[]> => {
  const { data, error } = await FetchClients.equipment.GET(
    "/equipment/site/{site_id}",
    {
      params: {
        path: { site_id: siteId },
        query: { include_preview, ...(building ? { building } : {}) },
      },
      headers,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }

  return recursiveCamelCaseCipher(data);
};

const getSiteEquipmentPage = async (
  siteId: number,
  params: SiteEquipmentPageParams = {},
  headers: EquipmentRequestHeaders = {}
): Promise<SiteEquipmentPageResponse> => {
  const { data, error } = await FetchClients.equipment.GET(
    "/equipment/site/{site_id}/page",
    {
      params: {
        path: { site_id: siteId },
        query: siteEquipmentPageParamsToQuery(params),
      },
      headers,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }

  return recursiveCamelCaseCipher(data) as SiteEquipmentPageResponse;
};

/**
 * Fetches all equipment matching the given filters in a single request
 * (`allow_all=true`). Used for CSV export and modals that need the full
 * filtered set — not for normal table browsing.
 *
 * Backend hard-limits to 10,000 rows; over that returns HTTP 400.
 */
async function fetchAllPages(
  siteId: number,
  params: SiteEquipmentPageParams,
  headers: EquipmentRequestHeaders = {}
): Promise<Equipment[]> {
  const { page: _page, pageSize: _pageSize, ...filterParams } = params;
  const response = await getSiteEquipmentPage(
    siteId,
    {
      ...filterParams,
      allowAll: true,
      includePreview: params.includePreview ?? false,
      includeSummary: false,
    },
    headers
  );

  return response.items;
}

function usePageQuery(
  siteId: number,
  params: SiteEquipmentPageParams,
  {
    refetchOnWindowFocus = true,
    enabled = true,
    keepPreviousData = false,
  }: {
    refetchOnWindowFocus?: boolean;
    enabled?: boolean;
    keepPreviousData?: boolean;
  } = {
    refetchOnWindowFocus: true,
    enabled: true,
  }
) {
  const equipmentSpecsHeaders = useEquipmentSpecsReadHeaders();
  return useQueryWithAccessCheckLog(
    ["Equipment", "read"],
    (checkFeatureAccessHeader) =>
      getSiteEquipmentPage(siteId, params, {
        ...checkFeatureAccessHeader,
        ...equipmentSpecsHeaders,
      }),
    {
      queryKey: [
        SITE_EQUIPMENT_PAGE_QUERY_KEY,
        siteId,
        params,
        equipmentSpecsHeaders[EQUIPMENT_SPECS_READS_HEADER_NAME],
      ],
      refetchOnWindowFocus,
      enabled,
      ...(keepPreviousData ? { placeholderData: keepPreviousQueryData } : {}),
    }
  );
}

function useQuery(
  siteId: number,
  {
    poll,
    include_preview,
    building,
  }: { poll?: boolean; include_preview?: boolean; building?: string | null } = {
    poll: false,
    include_preview: false,
  },
  {
    refetchOnWindowFocus = true,
    enabled = true,
    keepPreviousData = false,
  }: {
    refetchOnWindowFocus?: boolean;
    enabled?: boolean;
    keepPreviousData?: boolean;
  } = {
    refetchOnWindowFocus: true,
    enabled: true,
  }
) {
  const equipmentSpecsHeaders = useEquipmentSpecsReadHeaders();
  return useQueryWithAccessCheckLog(
    ["Equipment", "read"],
    (checkFeatureAccessHeader) =>
      getSiteEquipment(
        siteId,
        Boolean(include_preview),
        { ...checkFeatureAccessHeader, ...equipmentSpecsHeaders },
        building
      ),
    {
      queryKey: [
        SITE_EQUIPMENT_QUERY_KEY,
        siteId,
        include_preview,
        building,
        equipmentSpecsHeaders[EQUIPMENT_SPECS_READS_HEADER_NAME],
      ],
      refetchOnWindowFocus,
      refetchInterval: poll ? 1000 * 10 : false,
      enabled,
      ...(keepPreviousData ? { placeholderData: keepPreviousQueryData } : {}),
    }
  );
}

export const AIRTABLE_SYNC_DISABLED_INTERNAL_CODE =
  "equipment/airtable-sync-disabled";

// endpoint requires {feature: "Equipment", access: "edit"}
async function syncSiteEquipment(
  siteId: number,
  headers: EquipmentRequestHeaders
): Promise<void> {
  const { error } = await FetchClients.equipment.POST(
    "/equipment/sync/{site_id}",
    {
      params: {
        path: { site_id: siteId },
      },
      headers,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }
}

function useSyncEquipment(siteId: number) {
  const queryClient = useQueryClient();
  const equipmentSpecsRolloutEnabled = useEquipmentSpecsRolloutEnabled();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    (_variables: void, checkFeatureAccessHeader) =>
      syncSiteEquipment(siteId, {
        ...checkFeatureAccessHeader,
        ...(equipmentSpecsRolloutEnabled && {
          [EQUIPMENT_SPECS_WRITES_HEADER_NAME]: String(true),
        }),
      }),
    {
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [SITE_EQUIPMENT_QUERY_KEY, siteId],
          }),
          queryClient.invalidateQueries({
            queryKey: [SITE_EQUIPMENT_PAGE_QUERY_KEY, siteId],
          }),
        ]),
    }
  );
}

export type EquipmentSyncStatus =
  ClientPaths["equipment"]["/equipment/site/{site_id}/sync-status"]["get"]["responses"]["200"]["content"]["application/json"];

// endpoint requires {feature: "Equipment", access: "read"}
async function getEquipmentSyncStatus(
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.equipment.GET(
    "/equipment/site/{site_id}/sync-status",
    {
      params: {
        path: { site_id: siteId },
      },
      headers: checkFeatureAccessHeader,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }

  return data;
}

function useQuerySyncStatus(
  siteId: number,
  queryOpts?: Omit<UseQueryOptions<EquipmentSyncStatus>, "queryKey" | "queryFn">
) {
  return useQueryWithAccessCheckLog<EquipmentSyncStatus>(
    ["Equipment", "read"],
    (checkFeatureAccessHeader) =>
      getEquipmentSyncStatus(siteId, checkFeatureAccessHeader),
    {
      queryKey: ["equipment-sync-status", siteId],
      ...queryOpts,
    }
  );
}

export type EquipmentSyncPreview =
  ClientPaths["equipment"]["/equipment/sync-preview/{preview_id}"]["get"]["responses"]["200"]["content"]["application/json"];

export type EquipmentSyncPreviewResult = NonNullable<
  EquipmentSyncPreview["result"]
>;

// endpoint requires {feature: "Equipment", access: "edit"}
async function startSiteEquipmentSyncPreview(
  siteId: number,
  headers: EquipmentRequestHeaders
) {
  const { data, error } = await FetchClients.equipment.POST(
    "/equipment/site/{site_id}/sync-preview",
    {
      params: {
        path: { site_id: siteId },
      },
      headers,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }

  return data;
}

function useStartSyncPreview(siteId: number) {
  const equipmentSpecsRolloutEnabled = useEquipmentSpecsRolloutEnabled();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    (_variables: void, checkFeatureAccessHeader) =>
      startSiteEquipmentSyncPreview(siteId, {
        ...checkFeatureAccessHeader,
        ...(equipmentSpecsRolloutEnabled && {
          [EQUIPMENT_SPECS_WRITES_HEADER_NAME]: String(true),
        }),
      })
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function getEquipmentSyncPreview(
  previewId: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.equipment.GET(
    "/equipment/sync-preview/{preview_id}",
    {
      params: {
        path: { preview_id: previewId },
      },
      headers: checkFeatureAccessHeader,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }

  return data;
}

function useQuerySyncPreview(previewId: string | undefined, enabled = true) {
  return useQueryWithAccessCheckLog<EquipmentSyncPreview>(
    ["Equipment", "edit"],
    (checkFeatureAccessHeader) =>
      getEquipmentSyncPreview(previewId!, checkFeatureAccessHeader),
    {
      queryKey: ["equipment-sync-preview", previewId],
      enabled: previewId !== undefined && enabled,
      retry: 1,
      refetchInterval: (query) =>
        query.state.data?.status === "pending" ? 1000 : false,
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function applySiteEquipmentSyncPreview(
  previewId: string,
  headers: EquipmentRequestHeaders
) {
  const { error } = await FetchClients.equipment.POST(
    "/equipment/sync-preview/{preview_id}/apply",
    {
      params: {
        path: { preview_id: previewId },
      },
      headers,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }
}

function useApplySyncPreview(siteId: number) {
  const queryClient = useQueryClient();
  const equipmentSpecsRolloutEnabled = useEquipmentSpecsRolloutEnabled();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    (previewId: string, checkFeatureAccessHeader) =>
      applySiteEquipmentSyncPreview(previewId, {
        ...checkFeatureAccessHeader,
        ...(equipmentSpecsRolloutEnabled && {
          [EQUIPMENT_SPECS_WRITES_HEADER_NAME]: String(true),
        }),
      }),
    {
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [SITE_EQUIPMENT_QUERY_KEY, siteId],
          }),
          queryClient.invalidateQueries({
            queryKey: [SITE_EQUIPMENT_PAGE_QUERY_KEY, siteId],
          }),
        ]),
    }
  );
}

// NOTE: The `/equipment/site/{site_id}/export` endpoint does not honor the
// `X-Check-Feature-Access` header, so we intentionally do not send it here — it
// would be a no-op that implies enforcement that does not exist. Feature RBAC on
// this export is client-side only (the ExportAllCSVButton trigger is superAdmin-
// gated in the UI). Follow-up: make the export service honor/propagate the header.
// The equipment specs headers are a separate concern — they select which specs the
// response carries rather than enforcing access — so those are sent.
function useDownloadCSVExport(siteId: number, filename: string) {
  const equipmentSpecsHeaders = useEquipmentSpecsReadHeaders();
  return useCallback(async () => {
    const { data, error } = await FetchClients.equipment.GET(
      "/equipment/site/{site_id}/export",
      {
        params: {
          path: {
            site_id: siteId,
          },
        },
        parseAs: "blob",
        headers: equipmentSpecsHeaders,
      }
    );

    if (error) {
      throwBackpackResponseError(error);
    }

    const csvText = await data.text();
    const formattedCsv = formatEquipmentExportCsv(csvText);
    const newBlob = new Blob([formattedCsv]);
    const blobUrl = window.URL.createObjectURL(newBlob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  }, [siteId, filename, equipmentSpecsHeaders]);
}
